// Centralized stop-writing logic. Replaces drifted inline implementations
// across api/schwab/sync.js, ActionPanel.jsx, TradeDetailContent.jsx, and
// ImportCSV.jsx so that every stop write goes through the same path:
// stop_loss + initial_stop + risk_amount + r_multiple + stop_history.

const TRADE_SELECT_COLS =
  'id, avg_entry_price, quantity_total, quantity_open, initial_stop, ' +
  'asset_type, account_number, symbol, net_pnl, stop_loss'

// `||` (not `??`) so that initial_stop: 0 is treated as unset. Stops are
// always > 0 in this domain, so a zero value means "never been set".
function effectiveRiskStop(trade, stopPrice) {
  return trade.initial_stop || stopPrice
}

export function computeRisk(trade, stopPrice) {
  const riskStop = effectiveRiskStop(trade, stopPrice)
  const multiplier = trade.asset_type === 'option' ? 100 : 1
  const riskAmount = (trade.avg_entry_price && trade.quantity_total)
    ? Math.abs(trade.avg_entry_price - riskStop) * trade.quantity_total * multiplier
    : null
  const rMultiple = (trade.net_pnl != null && riskAmount != null && riskAmount > 0)
    ? trade.net_pnl / riskAmount
    : null
  return { riskAmount, rMultiple }
}

export async function recordStopChange(supabase, params) {
  const { tradeId, stopPrice, source, orderId, userId, accountNumber } = params
  let { trade } = params

  if (!trade) {
    const { data } = await supabase
      .from('trades')
      .select(TRADE_SELECT_COLS)
      .eq('id', tradeId)
      .single()
    trade = data
  }

  if (!trade) {
    return { updated: false, historyInserted: false, initialStopSet: false }
  }

  const acct = accountNumber || trade.account_number

  const updates = {
    stop_loss: stopPrice,
    updated_at: new Date().toISOString(),
  }

  let initialStopSet = false
  if (!trade.initial_stop) {
    updates.initial_stop = stopPrice
    initialStopSet = true
  }

  // Risk calc on the post-update trade (don't mutate the caller's object).
  const adjustedTrade = { ...trade, initial_stop: trade.initial_stop || stopPrice }
  const { riskAmount, rMultiple } = computeRisk(adjustedTrade, stopPrice)
  updates.risk_amount = riskAmount
  if (rMultiple != null) updates.r_multiple = rMultiple

  await supabase.from('trades').update(updates).eq('id', tradeId)

  // Dedup against the latest stop_history row for this trade+account.
  const { data: latest } = await supabase
    .from('stop_history')
    .select('stop_price')
    .eq('trade_id', tradeId)
    .eq('account_number', acct)
    .order('recorded_at', { ascending: false })
    .limit(1)

  const latestPrice = latest?.[0]?.stop_price != null ? Number(latest[0].stop_price) : null
  let historyInserted = false
  if (latestPrice !== Number(stopPrice)) {
    await supabase.from('stop_history').insert({
      user_id: userId,
      trade_id: tradeId,
      account_number: acct,
      symbol: trade.symbol,
      stop_price: stopPrice,
      previous_stop_price: trade.stop_loss ?? null,
      source,
      order_id: orderId || null,
      recorded_at: new Date().toISOString(),
    })
    historyInserted = true
  }

  return { updated: true, historyInserted, initialStopSet }
}

export async function recordStopChangeBatch(supabase, items) {
  if (!items || items.length === 0) {
    return { synced: 0, historyInserted: 0 }
  }

  let synced = 0
  // Per-item trade updates first. Errors on one item don't stop the rest.
  for (const item of items) {
    const { tradeId, stopPrice, trade } = item
    if (!trade) {
      console.warn(`[stopModule] batch item missing trade for tradeId=${tradeId} — skipping`)
      continue
    }
    try {
      const updates = {
        stop_loss: stopPrice,
        updated_at: new Date().toISOString(),
      }
      if (!trade.initial_stop) {
        updates.initial_stop = stopPrice
      }
      const adjustedTrade = { ...trade, initial_stop: trade.initial_stop || stopPrice }
      const { riskAmount, rMultiple } = computeRisk(adjustedTrade, stopPrice)
      updates.risk_amount = riskAmount
      if (rMultiple != null) updates.r_multiple = rMultiple

      const { error } = await supabase.from('trades').update(updates).eq('id', tradeId)
      if (error) {
        console.warn(`[stopModule] update failed for tradeId=${tradeId}:`, error.message)
        continue
      }
      synced++
    } catch (err) {
      console.warn(`[stopModule] update threw for tradeId=${tradeId}:`, err.message)
    }
  }

  // Single batch dedup query for stop_history across all affected trades.
  const tradeIds = [...new Set(items.map(i => i.tradeId))]
  const { data: latestStops } = await supabase
    .from('stop_history')
    .select('trade_id, account_number, stop_price')
    .in('trade_id', tradeIds)
    .order('recorded_at', { ascending: false })

  // Keying by trade_id alone caused dedup ping-pong when accounts under
  // the same trade had different stop prices (~8 phantom rows per cycle).
  const latestStopMap = {}
  for (const row of (latestStops || [])) {
    const key = `${row.trade_id}:${row.account_number}`
    if (!(key in latestStopMap)) {
      latestStopMap[key] = Number(row.stop_price)
    }
  }

  const recordedAt = new Date().toISOString()
  const newRecords = []
  for (const item of items) {
    const acct = item.accountNumber || item.trade?.account_number
    if (!acct) continue
    const key = `${item.tradeId}:${acct}`
    const lastPrice = latestStopMap[key]
    if (lastPrice !== undefined && lastPrice === Number(item.stopPrice)) continue

    newRecords.push({
      user_id: item.userId,
      trade_id: item.tradeId,
      account_number: acct,
      symbol: item.trade?.symbol,
      stop_price: item.stopPrice,
      previous_stop_price: lastPrice ?? (item.trade?.stop_loss ?? null),
      source: item.source,
      order_id: item.orderId || null,
      recorded_at: recordedAt,
    })
  }

  let historyInserted = 0
  if (newRecords.length > 0) {
    const { error } = await supabase.from('stop_history').insert(newRecords)
    if (!error) historyInserted = newRecords.length
  }

  return { synced, historyInserted }
}
